import handleGeneralError from '../helpers/errorHandler'
import { showToast } from './toastSlice'
import makeRequest from 'src/services/api'
import {
  createDistrictGeoLocationMasterDataUrl,
  createDivisionGeoLocationMasterDataUrl,
  createProvinceGeoLocationMasterDataUrl,
  createStateGeoLocationMasterDataUrl,
  fetchDistrictsGeolocationMasterDataUrl,
  fetchDivisionsGeolocationMasterDataUrl,
  fetchDivisionsUrl,
  fetchProvincesGeolocationMasterDataUrl,
  fetchProvincesUrl,
  fetchStatesGeolocationMasterDataUrl,
  fetchStatesUrl,
  updateDistrictGeoLocationMasterDataUrl,
  updateDivisionGeoLocationMasterDataUrl,
  updateProvinceGeoLocationMasterDataUrl,
  updateStateGeoLocationMasterDataUrl,
} from 'src/services/urls'

const { createSlice, createAsyncThunk } = require('@reduxjs/toolkit')

const initialState = {
  states: [],
  districts: [],
  provinces: [],
  divisions: [],
  docs: [],
  page: 0,
  totalDocs: 0,
  loading: false,
  error: '',
  sortBy: '',
  orderBy: '',
  filterBy: '',
}

export const fetchStatesForPortalMasterData = createAsyncThunk(
  'geoLocationMasterData/fetchStatesForPortalMasterData',
  async (options, { dispatch }) => {
    try {
      const page = options?.page || 1
      const limit = options?.limit || 10
      let url = fetchStatesGeolocationMasterDataUrl(page, limit)

      if (options?.state) {
        url += `&state=${options?.state}`
      }

      const res = await makeRequest(url)
      return res?.data?.data
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
    }
  },
)

export const fetchDistrictsForPortalMasterData = createAsyncThunk(
  'geoLocationMasterData/fetchDistrictsForPortalMasterData',
  async (options, { dispatch }) => {
    try {
      const page = options?.page || 1
      const limit = options?.limit || 10
      let url = fetchDistrictsGeolocationMasterDataUrl(page, limit)

      if (options?.district) {
        url += `&district=${options?.district}`
      }
      if (options?.states?.length) {
        let stateIds = options?.states?.map((s) => s.value).join(',') || ''
        url += `&state=${stateIds}`
      }
      if (options?.provinces?.length) {
        let provinceIds = options?.provinces?.map((s) => s.value).join(',') || ''
        url += `&province=${provinceIds}`
      }
      if (options?.divisions?.length) {
        let divisionIds = options?.divisions?.map((s) => s.value).join(',') || ''
        url += `&division=${divisionIds}`
      }

      const res = await makeRequest(url)
      return res?.data?.data
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
    }
  },
)

export const fetchProvincesForPortalMasterData = createAsyncThunk(
  'geoLocationMasterData/fetchProvincesForPortalMasterData',
  async (options, { dispatch }) => {
    try {
      const page = options?.page || 1
      const limit = options?.limit || 10
      let url = fetchProvincesGeolocationMasterDataUrl(page, limit)

      if (options?.province) {
        url += `&province=${options?.province}`
      }

      const res = await makeRequest(url)
      return res?.data?.data
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
    }
  },
)

export const fetchDivisionsForPortalMasterData = createAsyncThunk(
  'geoLocationMasterData/fetchDivisionsForPortalMasterData',
  async (options, { dispatch }) => {
    try {
      const page = options?.page || 1
      const limit = options?.limit || 10
      let url = fetchDivisionsGeolocationMasterDataUrl(page, limit)

      if (options?.division) {
        url += `&division=${options?.division}`
      }

      const res = await makeRequest(url)
      return res?.data?.data
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
    }
  },
)

export const fetchFilterOptions = createAsyncThunk(
  'geoLocationMasterData/fetchFilterOptions',
  async (data, { dispatch }) => {
    try {
      const [statesRes, divisionsRes, provincesRes] = await Promise.all([
        makeRequest(fetchStatesUrl()),
        makeRequest(fetchDivisionsUrl()),
        makeRequest(fetchProvincesUrl()),
      ])

      return {
        states: statesRes?.data?.data || statesRes?.data || [],
        divisions: divisionsRes?.data?.data || divisionsRes?.data || [],
        provinces: provincesRes?.data?.data || provincesRes?.data || [],
      }
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
    }
  },
)

export const createState = createAsyncThunk(
  'geoLocationMasterData/createState',
  async (data, { dispatch }) => {
    try {
      const res = await makeRequest(createStateGeoLocationMasterDataUrl(), 'POST', data)

      const { displayMessage } = res.data
      if (res.data.statusCode === 200) {
        dispatch(showToast({ title: 'Success', message: displayMessage, color: 'success' }))
        dispatch(
          fetchStatesForPortalMasterData({
            page: data?.page,
            limit: data?.pageSize,
            state: data?.stateFilter,
          }),
        )
      } else {
        dispatch(showToast({ title: 'Failed', message: displayMessage, color: 'danger' }))
      }
      return res.data
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
    }
  },
)

export const createDistrict = createAsyncThunk(
  'geoLocationMasterData/createDistrict',
  async (data, { dispatch }) => {
    try {
      const res = await makeRequest(createDistrictGeoLocationMasterDataUrl(), 'POST', data)

      const { displayMessage } = res.data
      if (res.data.statusCode === 200) {
        dispatch(showToast({ title: 'Success', message: displayMessage, color: 'success' }))
        dispatch(
          fetchDistrictsForPortalMasterData({
            page: data?.page,
            limit: data?.pageSize,
            district: data?.districtFilter,
            province: data?.provinceFilter,
            division: data?.divisionFilter,
            state: data?.stateFilter,
          }),
        )
      } else {
        dispatch(showToast({ title: 'Failed', message: displayMessage, color: 'danger' }))
      }
      return res.data
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
    }
  },
)

export const createProvince = createAsyncThunk(
  'geoLocationMasterData/createProvince',
  async (data, { dispatch }) => {
    try {
      const res = await makeRequest(createProvinceGeoLocationMasterDataUrl(), 'POST', data)

      const { displayMessage } = res.data
      if (res.data.statusCode === 200) {
        dispatch(showToast({ title: 'Success', message: displayMessage, color: 'success' }))
        dispatch(
          fetchProvincesForPortalMasterData({
            page: data?.page,
            limit: data?.pageSize,
            province: data?.provinceFilter,
          }),
        )
      } else {
        dispatch(showToast({ title: 'Failed', message: displayMessage, color: 'danger' }))
      }
      return res.data
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
    }
  },
)

export const createDivision = createAsyncThunk(
  'geoLocationMasterData/createDivision',
  async (data, { dispatch }) => {
    try {
      const res = await makeRequest(createDivisionGeoLocationMasterDataUrl(), 'POST', data)

      const { displayMessage } = res.data
      if (res.data.statusCode === 200) {
        dispatch(showToast({ title: 'Success', message: displayMessage, color: 'success' }))
        dispatch(
          fetchDivisionsForPortalMasterData({
            page: data?.page,
            limit: data?.pageSize,
            division: data?.divisionFilter,
          }),
        )
      } else {
        dispatch(showToast({ title: 'Failed', message: displayMessage, color: 'danger' }))
      }
      return res.data
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
    }
  },
)

export const updateState = createAsyncThunk(
  'geoLocationMasterData/updateState',
  async (data, { dispatch }) => {
    try {
      const id = data?.id
      let body = {
        state: data?.state,
        stateCode: data?.stateCode,
        uniqueStateId: data?.uniqueStateId,
      }

      const res = await makeRequest(updateStateGeoLocationMasterDataUrl(id), 'PATCH', body)
      console.log('res ', res)
      const { displayMessage, statusCode } = res.data
      if (statusCode === 200) {
        dispatch(showToast({ title: 'Success', message: displayMessage, color: 'success' }))
        dispatch(
          fetchStatesForPortalMasterData({
            page: data?.page,
            limit: data?.pageSize,
            state: data?.stateFilter,
          }),
        )
      } else {
        dispatch(showToast({ title: 'Failed', message: displayMessage, color: 'danger' }))
      }
      return res.data
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
    }
  },
)

export const updateDistrict = createAsyncThunk(
  'geoLocationMasterData/updateDistrict',
  async (data, { dispatch }) => {
    try {
      const id = data?.id
      const body = {
        name: data.name,
      }
      const res = await makeRequest(updateDistrictGeoLocationMasterDataUrl(id), 'PATCH', body)
      const { displayMessage } = res.data
      if (res.data.statusCode === 200) {
        dispatch(showToast({ title: 'Success', message: displayMessage, color: 'success' }))
        dispatch(
          fetchDistrictsForPortalMasterData({
            page: data?.page,
            limit: data?.pageSize,
            district: data?.districtFilter,
            province: data?.provinceFilter,
            division: data?.divisionFilter,
            state: data?.stateFilter,
          }),
        )
      } else {
        dispatch(showToast({ title: 'Failed', message: displayMessage, color: 'danger' }))
      }
      return res.data
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
      // const { message } = e?.response?.data?.error[0]
      // dispatch(showToast({ title: 'Failure', message: message, color: 'danger' }))
    }
  },
)

export const updateProvince = createAsyncThunk(
  'geoLocationMasterData/updateProvince',
  async (data, { dispatch }) => {
    try {
      const id = data?.id
      const body = {
        name: data.name,
      }
      const res = await makeRequest(updateProvinceGeoLocationMasterDataUrl(id), 'PATCH', body)

      const { displayMessage } = res.data
      if (res.data.statusCode === 200) {
        dispatch(showToast({ title: 'Success', message: displayMessage, color: 'success' }))
        dispatch(
          fetchProvincesForPortalMasterData({
            page: data?.page,
            limit: data?.pageSize,
            province: data?.provinceFilter,
          }),
        )
      } else {
        dispatch(showToast({ title: 'Failed', message: displayMessage, color: 'danger' }))
      }
      return res.data
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
      // const { message } = e?.response?.data?.error[0]
      // dispatch(showToast({ title: 'Failure', message: message, color: 'danger' }))
    }
  },
)

export const updateDivision = createAsyncThunk(
  'geoLocationMasterData/updateDivision',
  async (data, { dispatch }) => {
    try {
      const id = data?.id
      const body = {
        name: data.name,
      }
      const res = await makeRequest(updateDivisionGeoLocationMasterDataUrl(id), 'PATCH', body)

      const { displayMessage } = res.data
      if (res.data.statusCode === 200) {
        dispatch(showToast({ title: 'Success', message: displayMessage, color: 'success' }))
        dispatch(
          fetchDivisionsForPortalMasterData({
            page: data?.page,
            limit: data?.pageSize,
            division: data?.divisionFilter,
          }),
        )
      } else {
        dispatch(showToast({ title: 'Failed', message: displayMessage, color: 'danger' }))
      }
      return res.data
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
      // const { message } = e?.response?.data?.error[0]
      // dispatch(showToast({ title: 'Failure', message: message, color: 'danger' }))
    }
  },
)

const geoLocationMasterSlice = createSlice({
  name: 'GeoLocationMasterData',
  initialState,
  reducers: {
    setLoading(state, action) {
      state.loading = action.payload
    },
    setSortBy(state, action) {
      state.sortBy = action.payload
    },
    setFilterBy(state, action) {
      state.filterBy = action.payload
    },
    setOrderBy(state, action) {
      state.orderBy = action.payload
    },
    setPage(state, action) {
      state.page = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchDistrictsForPortalMasterData.pending, (state, action) => {
      state.loading = true
      state.docs = []
    })
    builder.addCase(fetchDistrictsForPortalMasterData.fulfilled, (state, action) => {
      state.loading = false
      if (!action.payload.error) {
        state.docs = action.payload.data
        state.totalDocs = action.payload.totalDocs
      }
    })
    builder.addCase(fetchDistrictsForPortalMasterData.rejected, (state, action) => {
      state.loading = false
      state.docs = []
      state.error = action.error.message
    })
    builder.addCase(fetchDivisionsForPortalMasterData.pending, (state, action) => {
      state.loading = true
      state.docs = []
    })
    builder.addCase(fetchDivisionsForPortalMasterData.fulfilled, (state, action) => {
      state.loading = false
      if (!action.payload.error) {
        state.docs = action.payload.data
        state.totalDocs = action.payload.totalDocs
      }
    })
    builder.addCase(fetchDivisionsForPortalMasterData.rejected, (state, action) => {
      state.loading = false
      state.docs = []
      state.error = action.error.message
    })
    builder.addCase(fetchProvincesForPortalMasterData.pending, (state, action) => {
      state.loading = true
      state.docs = []
    })
    builder.addCase(fetchProvincesForPortalMasterData.fulfilled, (state, action) => {
      state.loading = false
      if (!action.payload.error) {
        state.docs = action.payload.data
        state.totalDocs = action.payload.totalDocs
      }
    })
    builder.addCase(fetchProvincesForPortalMasterData.rejected, (state, action) => {
      state.loading = false
      state.docs = []
      state.error = action.error.message
    })
    builder.addCase(fetchStatesForPortalMasterData.pending, (state, action) => {
      state.loading = true
      state.docs = []
    })
    builder.addCase(fetchStatesForPortalMasterData.fulfilled, (state, action) => {
      state.loading = false
      if (!action.payload.error) {
        state.docs = action.payload.data
        state.totalDocs = action.payload.totalDocs
      }
    })
    builder.addCase(fetchStatesForPortalMasterData.rejected, (state, action) => {
      state.loading = false
      state.docs = []
      state.error = action.error.message
    })
    builder.addCase(fetchFilterOptions.fulfilled, (state, action) => {
      state.states = action.payload.states
      state.divisions = action.payload.divisions
      state.provinces = action.payload.provinces
    })
  },
})

export default geoLocationMasterSlice.reducer
export const { setPage, setSortBy, setOrderBy, setFilterBy } = geoLocationMasterSlice.actions
